import { useEffect } from 'react';
import { isElementWithin } from '../dom/isElementWithin';

/**
 * Gets the element under the cursor for a pointer event, handling body's pointer-events: none
 * (common with Radix popovers). Uses elementFromPoint instead of e.target for reliability.
 */
export function getTargetFromPointerEvent(e: PointerEvent): HTMLElement | null {
  const body = document.body;
  const originalPointerEvents = body.style.pointerEvents;
  let target: HTMLElement | null = null;

  try {
    if (body.style.pointerEvents === 'none') {
      body.style.pointerEvents = 'auto';
    }
    target = document.elementFromPoint(
      e.clientX,
      e.clientY
    ) as HTMLElement | null;
  } finally {
    body.style.pointerEvents = originalPointerEvents;
  }

  return target;
}

/**
 * Custom hook to fix the Radix UI DismissableLayer issue where clicking on elements
 * that stop propagation requires 2 outside clicks to close the layer.
 *
 * @see https://github.com/radix-ui/primitives/issues/2782
 *
 * @param isOpen - Whether the dismissable layer is currently open
 * @param onClose - Callback to close the dismissable layer
 * @param selectors - Array of CSS selectors for elements that should be considered "inside" the layer
 */
export function useDismissableLayerFix(
  isOpen: boolean,
  onClose: () => void,
  selectors: string[] = []
) {
  useEffect(() => {
    if (!isOpen) return;

    function handleDocumentPointerDown(e: PointerEvent) {
      const target = getTargetFromPointerEvent(e);
      if (!target) return;

      // Check if click target is inside any of the specified selectors
      const isInsideLayer = selectors.some(selector => {
        const layerElement = document.querySelector(selector) as HTMLElement;
        return layerElement && isElementWithin(layerElement, target!);
      });

      // If click is outside all specified elements, check for nested expanded elements before closing
      if (!isInsideLayer) {
        // Check if there's any aria-expanded="true" element that is virtually contained within our layer
        const hasNestedExpandedElement = selectors.some(selector => {
          const layerElement = document.querySelector(selector) as HTMLElement;
          if (!layerElement) return false;

          // Find all aria-expanded="true" elements
          const expandedElements = document.querySelectorAll(
            '[aria-expanded="true"]'
          );

          return Array.from(expandedElements).some(expandedElement => {
            // Check if this expanded element is virtually contained within our layer
            return isElementWithin(
              layerElement,
              expandedElement as HTMLElement
            );
          });
        });

        // Only close if there are no nested expanded elements
        if (!hasNestedExpandedElement) {
          onClose();
        }
      }
    }

    document.addEventListener('pointerdown', handleDocumentPointerDown, true);

    return () => {
      document.removeEventListener(
        'pointerdown',
        handleDocumentPointerDown,
        true
      );
    };
  }, [isOpen, onClose, selectors]);
}
